import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { sendWhatsAppForm } from "./utils/whatsapp";
import "./App.css";

const Home = lazy(() => import("./Home"));
const Services = lazy(() => import("./pages/Services"));
const OurWork = lazy(() => import("./pages/OurWork"));
const Careers = lazy(() => import("./pages/Careers"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const NotFound = lazy(() => import("./pages/NotFound"));

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);

  return null;
}

function ImageSelectionManager() {
  useEffect(() => {
    const groupSelector = ".interactive-image-group";
    const cardSelector = ".interactive-image-card";

    const clearGroupSelection = (group) => {
      group.classList.remove("has-active-image");
      group.querySelectorAll(`${cardSelector}.is-selected`).forEach((card) => {
        card.classList.remove("is-selected");
      });
    };

    const activateCard = (card) => {
      const group = card.closest(groupSelector);
      if (!group) {
        return;
      }

      const wasSelected = card.classList.contains("is-selected");
      clearGroupSelection(group);

      if (!wasSelected) {
        group.classList.add("has-active-image");
        card.classList.add("is-selected");
      }
    };

    const onClick = (event) => {
      const card = event.target.closest(cardSelector);
      if (card) {
        activateCard(card);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        document.querySelectorAll(groupSelector).forEach(clearGroupSelection);
        return;
      }

      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      const card = event.target.closest(cardSelector);
      if (!card) {
        return;
      }

      event.preventDefault();
      activateCard(card);
    };

    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return null;
}

function ScrollRevealManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let observer;
    let mutationObserver;
    let rafId = 0;
    const revealSelector = [
      ".site-header .nav-wrap",
      ".site-footer",
      ".site-footer .footer-grid > section",
      ".section",
      ".panel",
      ".card-grid > *",
      ".image-explain-card",
      ".detailed-service",
      ".service-tile",
      ".feature-card",
      ".capability-card",
      ".expertise-card",
      ".stat-card",
      ".our-work-card",
      ".blog-card",
      ".modern-card",
      ".new-middle-banner",
      ".new-home-service-card",
      ".new-home-stat-card",
      ".new-home-google-card",
      ".new-home-form",
      ".welcome-popup",
      ".section-card"
    ].join(",");

    const collectTargets = () => Array.from(document.querySelectorAll(revealSelector));

    const applyToTargets = (targets) => {
      if (!targets.length) {
        return;
      }

      targets.forEach((node, index) => {
        if (node.classList.contains("reveal-ready")) {
          return;
        }

        node.classList.add("reveal-ready");
        node.style.setProperty("--reveal-delay", `${Math.min((index % 7) * 65, 390)}ms`);

        if (prefersReducedMotion || typeof IntersectionObserver === "undefined") {
          node.classList.add("reveal-visible");
          return;
        }

        observer.observe(node);
      });
    };

    const scheduleScan = () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      rafId = window.requestAnimationFrame(() => {
        applyToTargets(collectTargets());
      });
    };

    if (!prefersReducedMotion && typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }
            entry.target.classList.add("reveal-visible");
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.14, rootMargin: "0px 0px -8% 0px" }
      );
    }

    const timeoutId = window.setTimeout(scheduleScan, 90);

    mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length) {
          scheduleScan();
          break;
        }
      }
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.clearTimeout(timeoutId);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, [pathname]);

  return null;
}

function GlobalConnectPopup() {
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    setShowPopup(true);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowPopup(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  if (!showPopup) {
    return null;
  }

  return (
    <div
      className="connect-popup-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome and contact form"
      onClick={() => setShowPopup(false)}
    >
      <div className="connect-popup-card" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          className="connect-popup-close"
          aria-label="Close popup"
          onClick={() => setShowPopup(false)}
        >
          x
        </button>

        <div className="connect-popup-visual" aria-hidden="true">
          <img src="/assets/TP visuals gold.png" alt="" />
          <p>Premium Event Production</p>
        </div>

        <div className="connect-popup-content">
          <p className="connect-popup-kicker">Welcome</p>
          <h2>Let&apos;s Connect For Your Next Event</h2>
          <p>
            Share your requirement and our team will get back with a tailored production plan.
          </p>

          <form
            className="connect-popup-form"
            onSubmit={(event) => {
              sendWhatsAppForm({ event, heading: "New Enquiry (Popup Form)" });
              setShowPopup(false);
            }}
          >
            <div className="connect-popup-form-grid">
              <label>
                Full Name
                <input type="text" name="name" placeholder="Your full name" required />
              </label>
              <label>
                Email
                <input type="email" name="email" placeholder="Email" required />
              </label>
              <label>
                Phone Number
                <input type="tel" name="phone" placeholder="+91 98XXXXXXXX" required />
              </label>
              <label>
                Event Type
                <select name="eventType" defaultValue="" required>
                  <option value="" disabled>Select event type</option>
                  <option value="conference">Conference</option>
                  <option value="product-launch">Product Launch</option>
                  <option value="annual-day">Annual Day</option>
                  <option value="virtual-event">Virtual Event</option>
                  <option value="other">Other</option>
                </select>
              </label>
            </div>
            <label>
              Project Brief
              <textarea name="message" rows="3" placeholder="City, audience size, date and goals." required />
            </label>
            <button type="submit" className="btn-primary">Send Request</button>
          </form>
        </div>
      </div>
    </div>
  );
}

function AppLayout() {
  return (
    <div className="site-shell">
      <GlobalConnectPopup />
      <Navbar />
      <main className="site-main">
        <Suspense fallback={<div className="page-loading">Loading...</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/our-work" element={<OurWork />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/clientele" element={<Gallery />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <ImageSelectionManager />
      <ScrollRevealManager />
      <AppLayout />
    </Router>
  );
}

export default App;
